import { Messaging, onMessage } from 'firebase/messaging';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useToast } from '@app/components/ui/Toast';
import { firebaseService } from '@app/services/firebase.service';
import { notificationsService } from '@app/services/notifications.service';

import { AuthState, useAuth } from './useAuth';

type FirebaseContextValue = {
  lastMessage: any;
  token: string;
  permission: NotificationPermission;
  notifications: number;
  requestPermission: () => void;
};

const FirebaseContext = createContext<FirebaseContextValue | undefined>(undefined);

export function FirebaseProvider({ children }: { children: React.ReactNode }) {
  const toast = useToast();
  const { isAuth } = useAuth();
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [token, setToken] = useState<string>('');
  const [messaging, setMessaging] = useState<Messaging | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [serviceWorker, setServiceWorker] = useState<ServiceWorkerRegistration | null>(null);
  const [isInitialized, setIsInitialized] = useState(true);
  const [notifications, setNotifications] = useState<number>(0);

  const getNotifications = async () => {
    const { success, errors, totalItem } = await notificationsService.getNotifications(
      'unread',
      1,
      20
    );
    if (!success || errors) return;

    setNotifications(totalItem || 0);
  };

  const unsubscribe = useCallback(() => {
    if (!messaging) return;

    return onMessage(messaging, async (payload: any) => {
      const { title, body }: { title: string; body: string } = payload?.notification || {};
      if (!title || !body) return;

      if (permission === 'granted') {
        const notification = new Notification(title, {
          body,
          icon: '/images/logo.svg',
        });

        notification.onerror = () => {
          toast.success(body, title);
        };

        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      } else {
        toast.success(body, title);
      }

      getNotifications();
      setLastMessage(payload);
    });
  }, [messaging]);

  const requestPermission = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (permission === 'granted') return;

    Notification.requestPermission().then(permission => {
      setPermission(permission);
    });
  }, [permission]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    if (!isInitialized) return;

    setIsInitialized(false);
    navigator.serviceWorker.register('/firebase-messaging-sw.js').then(registration => {
      setServiceWorker(registration);
    });

    return () => {
      serviceWorker?.unregister();
    };
  }, []);

  useEffect(() => {
    if (isAuth !== AuthState.AUTH) {
      setNotifications(0);
    } else {
      getNotifications();
    }

    (async () => {
      const messaging = await firebaseService.getMessaging();
      if (!messaging) {
        console.log('FCM not supported on this browser');

        return;
      }

      setMessaging(messaging);
    })();
  }, [isAuth]);

  useEffect(() => {
    if (permission === 'default' && typeof window !== 'undefined') {
      Notification.requestPermission().then(permission => {
        setPermission(permission);
      });
    } else {
      setPermission(Notification.permission);
    }
  }, [permission]);

  useEffect(() => {
    if (isAuth !== AuthState.AUTH) return;
    if (!serviceWorker || permission !== 'granted') return;

    (async () => {
      const token = await firebaseService.getToken(serviceWorker);
      if (!token) return;

      await notificationsService.registerDevice(token);

      setToken(token);
    })();

    return unsubscribe();
  }, [isAuth, permission, serviceWorker]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (permission !== 'granted') return;
    if (!lastMessage) return;

    const notification = new Notification(lastMessage.title, {
      body: lastMessage.notification.body,
      icon: '/images/logo.svg',
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return () => {
      notification.close();
    };
  }, [lastMessage, permission]);

  const value = useMemo(
    () => ({
      lastMessage,
      token,
      permission,
      notifications,
      requestPermission,
    }),
    [lastMessage, token, permission, notifications, requestPermission]
  );

  return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
}

export function useFirebase() {
  const context = useContext(FirebaseContext);

  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }

  return context;
}
